import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../axiosBaseQuery';

export const fieldsApi = createApi({
  reducerPath: 'fieldsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Fields', 'Field', 'Survey'],
  endpoints: build => ({
    getFields: build.query({
      query: () => ({ url: '/fields' }),
      providesTags: ['Fields']
    }),
    getField: build.query({
      query: fieldId => ({ url: `/fields/${fieldId}` }),
      providesTags: (result, error, fieldId) => [{ type: 'Field', id: fieldId }]
    }),
    getSurvey: build.query({
      query: surveyId => ({ url: `/surveys/${surveyId}` }),
      providesTags: (result, error, surveyId) => [{ type: 'Survey', id: surveyId }]
    }),
    finaliseSurvey: build.mutation({
      query: ({ fieldId, surveyId }) => ({
        url: `/fields/${fieldId}/surveys/finalise`,
        method: 'post',
        data: { survey_id: surveyId },
        skipErrorModal: true
      }),
      invalidatesTags: (result, error, { fieldId, surveyId }) => [
        'Fields',
        { type: 'Field', id: fieldId },
        { type: 'Survey', id: surveyId }
      ]
    }),
    getSurveyCoverage: build.query({
      query: surveyId => ({ url: `/surveys/${surveyId}/coverage` }),
      providesTags: (result, error, surveyId) => [{ type: 'Survey', id: surveyId }]
    }),
    getSurveyTracks: build.query({
      query: surveyId => ({ url: `/surveys/${surveyId}/tracks` }),
      providesTags: (result, error, surveyId) => [{ type: 'Survey', id: surveyId }]
    }),
    getSurveyStones: build.query({
      query: surveyId => ({ url: `/surveys/${surveyId}/stones` }),
      providesTags: (result, error, surveyId) => [{ type: 'Survey', id: surveyId }]
    }),
    getPickListPreview: build.query({
      query: ({ fieldId, capPerAcre }) => ({
        url: `/fields/${fieldId}/pick-list/preview`,
        method: 'post',
        data: { cap_per_acre: capPerAcre },
        skipErrorModal: true
      })
    }),
    updateSurvey: build.mutation({
      query: ({ surveyId, mustPickCm }) => ({
        url: `/surveys/${surveyId}`,
        method: 'patch',
        data: { must_pick_cm: mustPickCm },
        skipErrorModal: true
      }),
      async onQueryStarted({ fieldId, surveyId }, { dispatch, queryFulfilled }) {
        let saved;
        try {
          ({ data: saved } = await queryFulfilled);
        } catch (error) {
          return;
        }
        const mustPickCm = saved.survey.must_pick_cm;
        dispatch(
          fieldsApi.util.updateQueryData('getField', fieldId, draft => {
            const survey = draft.surveys?.find(({ id }) => id === surveyId);
            if (survey) survey.must_pick_cm = mustPickCm;
          })
        );
        dispatch(
          fieldsApi.util.updateQueryData('getSurvey', surveyId, draft => {
            if (draft.survey) draft.survey.must_pick_cm = mustPickCm;
          })
        );
      }
    }),
    getStone: build.query({
      query: stoneId => ({ url: `/stones/${stoneId}`, skipErrorModal: true })
    })
  })
});

export const {
  useGetFieldsQuery,
  useGetFieldQuery,
  useGetSurveyQuery,
  useFinaliseSurveyMutation,
  useGetSurveyCoverageQuery,
  useGetSurveyTracksQuery,
  useGetSurveyStonesQuery,
  useGetPickListPreviewQuery,
  useUpdateSurveyMutation,
  useGetStoneQuery
} = fieldsApi;
